import React, { useCallback, useEffect, useState } from 'react';
import { getActivityLog } from '../../services/purchaseProgramApi';

const PAGE_SIZE = 50;

const ACTION_COLORS = {
  session_start:    { bg: '#e0f2fe', color: '#0369a1', icon: 'bi-play-circle' },
  add_to_cart:      { bg: '#dcfce7', color: '#15803d', icon: 'bi-cart-plus' },
  remove_from_cart: { bg: '#fee2e2', color: '#b91c1c', icon: 'bi-cart-dash' },
  qty_changed:      { bg: '#fef9c3', color: '#92400e', icon: 'bi-pencil' },
  checkout_started: { bg: '#ede9fe', color: '#6d28d9', icon: 'bi-bag' },
  order_placed:     { bg: '#bbf7d0', color: '#166534', icon: 'bi-check-circle-fill' },
  order_failed:     { bg: '#fee2e2', color: '#991b1b', icon: 'bi-x-circle-fill' },
};

function ActionBadge({ type }) {
  const s = ACTION_COLORS[type] || { bg: '#f1f5f9', color: '#475569', icon: 'bi-dot' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: s.bg, color: s.color, borderRadius: 20,
      padding: '2px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      <i className={`bi ${s.icon}`} /> {type.replace(/_/g, ' ')}
    </span>
  );
}

function fmtDt(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function fmt$(v) {
  if (v == null || v === '') return '';
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ActivityLogTab({ programs }) {
  const [filters, setFilters] = useState({
    programId: '', userId: '', proxyUserId: '', customerId: '', shipToId: '',
    sessionId: '', actionType: '', dateFrom: '', dateTo: '',
  });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);

  const load = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const res = await getActivityLog({
        programId: filters.programId ? Number(filters.programId) : undefined,
        userId: filters.userId || undefined,
        proxyUserId: filters.proxyUserId || undefined,
        customerId: filters.customerId || undefined,
        shipToId: filters.shipToId || undefined,
        sessionId: filters.sessionId || undefined,
        actionType: filters.actionType || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        limit: PAGE_SIZE,
        offset: off,
      });
      setItems(res?.items || []);
      setTotal(res?.total || 0);
      setOffset(off);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(0); }, [load]);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div>
      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-3 col-sm-6">
              <label className="form-label fw-semibold small mb-1">Program</label>
              <select className="form-select form-select-sm" value={filters.programId} onChange={e => set('programId', e.target.value)}>
                <option value="">All programs</option>
                {(programs || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="col-md-2 col-sm-6">
              <label className="form-label fw-semibold small mb-1">Action</label>
              <select className="form-select form-select-sm" value={filters.actionType} onChange={e => set('actionType', e.target.value)}>
                <option value="">All actions</option>
                {Object.keys(ACTION_COLORS).map(k => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="col-md-2 col-sm-6">
              <label className="form-label fw-semibold small mb-1">User ID</label>
              <input className="form-control form-control-sm" placeholder="user123" value={filters.userId} onChange={e => set('userId', e.target.value)} />
            </div>
            <div className="col-md-2 col-sm-6">
              <label className="form-label fw-semibold small mb-1">
                <i className="bi bi-person-fill-gear me-1 text-purple-600" />Proxy User
              </label>
              <input className="form-control form-control-sm" placeholder="admin-id" value={filters.proxyUserId} onChange={e => set('proxyUserId', e.target.value)} />
            </div>
            <div className="col-md-2 col-sm-6">
              <label className="form-label fw-semibold small mb-1">Customer ID</label>
              <input className="form-control form-control-sm" placeholder="CUST001" value={filters.customerId} onChange={e => set('customerId', e.target.value)} />
            </div>
            <div className="col-md-1 col-sm-6">
              <label className="form-label fw-semibold small mb-1">Site</label>
              <input className="form-control form-control-sm" placeholder="SITE01" value={filters.shipToId} onChange={e => set('shipToId', e.target.value)} />
            </div>
            <div className="col-md-2 col-sm-6">
              <label className="form-label fw-semibold small mb-1">Session ID</label>
              <input className="form-control form-control-sm" placeholder="uuid" value={filters.sessionId} onChange={e => set('sessionId', e.target.value)} />
            </div>
            <div className="col-md-2 col-sm-6">
              <label className="form-label fw-semibold small mb-1">From date</label>
              <input type="date" className="form-control form-control-sm" value={filters.dateFrom} onChange={e => set('dateFrom', e.target.value)} />
            </div>
            <div className="col-md-2 col-sm-6">
              <label className="form-label fw-semibold small mb-1">To date</label>
              <input type="date" className="form-control form-control-sm" value={filters.dateTo} onChange={e => set('dateTo', e.target.value)} />
            </div>
            <div className="col-md-2 col-sm-6 d-flex align-items-end gap-2">
              <button className="btn btn-primary btn-sm" onClick={() => load(0)} disabled={loading}>
                <i className="bi bi-search me-1" />{loading ? 'Loading…' : 'Search'}
              </button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setFilters({
                programId: '', userId: '', proxyUserId: '', customerId: '', shipToId: '',
                sessionId: '', actionType: '', dateFrom: '', dateTo: '',
              })}>
                <i className="bi bi-x-circle me-1" />Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Summary bar ──────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="text-secondary small">
          <strong>{total.toLocaleString()}</strong> event{total !== 1 ? 's' : ''} found
        </span>
        {totalPages > 1 && (
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-secondary btn-sm py-0" disabled={currentPage <= 1} onClick={() => load(offset - PAGE_SIZE)}>
              <i className="bi bi-chevron-left" />
            </button>
            <span className="small">Page {currentPage} of {totalPages}</span>
            <button className="btn btn-outline-secondary btn-sm py-0" disabled={currentPage >= totalPages} onClick={() => load(offset + PAGE_SIZE)}>
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        )}
      </div>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="text-center py-5 text-secondary">
          <div className="spinner-border spinner-border-sm me-2" />Loading activity…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-5 text-secondary">
          <i className="bi bi-activity fs-3 d-block mb-2" />No activity events found.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle" style={{ fontSize: 13 }}>
            <thead className="table-light">
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>User</th>
                <th><i className="bi bi-person-fill-gear me-1 text-warning" />Proxy</th>
                <th>Customer / Site</th>
                <th>Program</th>
                <th>Product</th>
                <th>Qty</th>
                <th>Cart</th>
                <th>Session</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const isSessStart = item.action_type === 'session_start';
                const hasProxy = !!item.proxy_user_id;
                return (
                  <tr
                    key={item.id}
                    style={{ background: isSessStart ? '#f8fafc' : undefined }}
                    title={item.session_id ? `Session: ${item.session_id}` : ''}
                  >
                    <td style={{ whiteSpace: 'nowrap', color: '#64748b' }}>{fmtDt(item.created_at)}</td>
                    <td><ActionBadge type={item.action_type} /></td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{item.user_id}</td>
                    <td>
                      {hasProxy ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <i className="bi bi-person-fill-gear text-warning" />
                          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{item.proxy_user_name || item.proxy_user_id}</span>
                        </span>
                      ) : (
                        <span className="text-secondary">—</span>
                      )}
                    </td>
                    <td>
                      {item.customer_id && <span className="badge bg-info text-dark me-1" style={{ fontSize: 11 }}>{item.customer_id}</span>}
                      {item.ship_to_id && (
                        <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
                          <i className="bi bi-geo-alt me-1" />{item.ship_to_id}
                        </span>
                      )}
                      {!item.customer_id && !item.ship_to_id && <span className="text-secondary">—</span>}
                    </td>
                    <td>{item.program_name || (item.program_id ? `#${item.program_id}` : '—')}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{item.product_code || '—'}</td>
                    <td className="text-center">{item.quantity ?? '—'}</td>
                    <td>
                      {item.cart_total != null ? (
                        <span>{fmt$(item.cart_total)}{item.cart_item_count != null ? ` (${item.cart_item_count})` : ''}</span>
                      ) : '—'}
                    </td>
                    <td>
                      {item.session_id ? (
                        <button
                          className="btn btn-link btn-sm p-0"
                          style={{ fontSize: 11, fontFamily: 'monospace' }}
                          onClick={() => setExpandedSession(s => s === item.session_id ? null : item.session_id)}
                          title="Filter by this session"
                        >
                          {item.session_id.slice(0, 8)}…
                        </button>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Session drill-down panel */}
      {expandedSession && (
        <SessionDrillDown
          sessionId={expandedSession}
          onClose={() => setExpandedSession(null)}
        />
      )}
    </div>
  );
}

function SessionDrillDown({ sessionId, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getActivityLog({ sessionId, limit: 200, offset: 0 })
      .then(r => setItems(r?.items || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [sessionId]);

  function fmtDt(dt) {
    if (!dt) return '—';
    return new Date(dt).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  }

  return (
    <div className="card border-primary mt-3">
      <div className="card-header d-flex align-items-center justify-content-between bg-primary text-white py-2">
        <span>
          <i className="bi bi-clock-history me-2" />
          Session timeline: <code className="text-white ms-1" style={{ fontSize: 12 }}>{sessionId}</code>
        </span>
        <button className="btn btn-sm btn-outline-light py-0" onClick={onClose}>
          <i className="bi bi-x-lg" />
        </button>
      </div>
      <div className="card-body p-0">
        {loading ? (
          <div className="text-center py-4 text-secondary"><div className="spinner-border spinner-border-sm me-2" />Loading…</div>
        ) : (
          <div className="timeline-container p-3" style={{ position: 'relative' }}>
            {items.length === 0 ? <p className="text-secondary mb-0">No events found.</p> : (
              <ol className="list-unstyled mb-0" style={{ borderLeft: '2px solid #e2e8f0', paddingLeft: 24, position: 'relative' }}>
                {[...items].reverse().map((ev, i) => (
                  <li key={ev.id} style={{ position: 'relative', marginBottom: 16 }}>
                    <span style={{
                      position: 'absolute', left: -33, top: 4,
                      width: 12, height: 12, borderRadius: '50%',
                      background: ACTION_COLORS[ev.action_type]?.color || '#94a3b8',
                      border: '2px solid white', boxShadow: '0 0 0 2px #e2e8f0',
                    }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <ActionBadge type={ev.action_type} />
                      <span style={{ fontSize: 11, color: '#64748b' }}>{fmtDt(ev.created_at)}</span>
                      {ev.product_code && <code style={{ fontSize: 11 }}>{ev.product_code}</code>}
                      {ev.quantity != null && <span style={{ fontSize: 12 }}>×{ev.quantity}</span>}
                      {ev.cart_total != null && (
                        <span style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>
                          cart: ${Number(ev.cart_total).toFixed(2)}
                        </span>
                      )}
                      {ev.proxy_user_id && (
                        <span style={{ fontSize: 11, background: '#fef3c7', padding: '1px 7px', borderRadius: 10 }}>
                          <i className="bi bi-person-fill-gear me-1" />{ev.proxy_user_name || ev.proxy_user_id}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
